import { useEffect, useRef } from 'react';
import type { Dispatch, MouseEvent, SetStateAction } from 'react';
import { useTranslations } from 'next-intl';

import { useToolContext } from '@/context/ToolContext';
import type { ItemType } from '@/config/itemTypes';

interface FieldsProps {
  setField: Dispatch<SetStateAction<ItemType | null>>;
  fieldTypes: ItemType[];
  fieldSelected?: string;
  isMobile?: boolean;
}

const Fields = ({
  setField,
  fieldTypes,
  fieldSelected,
  isMobile,
}: FieldsProps) => {
  const t = useTranslations();
  const ToolContextState = useToolContext();
  const switchRef = useRef<HTMLInputElement>(null);

  const handleFieldClick = (event: MouseEvent<HTMLButtonElement>) => {
    const selectedVariation = (event.target as HTMLElement).dataset.variation;
    const field = fieldTypes.find(
      (item) => item.variation === selectedVariation,
    );
    setField(field!);
  };

  const setType = (isIndoor: boolean) => {
    ToolContextState.setIsIndoorActive(isIndoor);

    const newField = isIndoor
      ? fieldSelected!.replace('field', 'room')
      : fieldSelected!.replace('room', 'field');
    const field = fieldTypes.find((item) => item.variation === newField);
    ToolContextState.setFieldSelected(field!);

    ToolContextState.setDrawingType(isIndoor ? 'indoor' : 'outdoor');
  };

  useEffect(() => {
    // Toggle the switch if field changes on mobile
    if (switchRef.current)
      switchRef.current.checked = ToolContextState.isIndoorActive;
  }, [ToolContextState.isIndoorActive]);

  return (
    <section id="Fields-container" className="Section Fields">
      <div className="Fields-header">
        <h2 className="Section-heading">{t('DRWNG__MENU_FIELDS')}</h2>
        {/* Dugout has no indoor/outdoor (field↔room) switch — the pitch is a `pitchType` chosen by
            the host, and all four fields (incl. Futsal) show as a flat grid. Gated so the other
            themes keep the Zaalvoetbal switch exactly as-is. */}
        {!isMobile && !ToolContextState.isDugoutTheme && (
          <div className="Fields-indoor">
            <label
              htmlFor="Futsal-switch"
              className={`Fields-switchLabel ${ToolContextState.drawingType === 'indoor' ? 'is-indoor' : ''}`}
            >
              {t('DRWNG__MENU_FUTSAL')}
              <div className="Fields-switch">
                <input
                  id="Futsal-switch"
                  type="checkbox"
                  aria-label={t('DRWNG__MENU_FUTSAL')}
                  ref={switchRef}
                  onChange={(e) => {
                    setType(e.target.checked);
                  }}
                />
                <span className="Fields-slider"></span>
              </div>
            </label>
          </div>
        )}
      </div>
      <div className="Section-inner">
        {fieldTypes
          .filter((item) =>
            isMobile || ToolContextState.isDugoutTheme
              ? item
              : ToolContextState.isIndoorActive
                ? item.type === 'indoor'
                : item.type === 'outdoor',
          )
          .map((item) => (
            <div
              className={isMobile ? 'Item-container' : ''}
              key={item.variation}
            >
              <button
                onClick={handleFieldClick}
                data-variation={item.variation}
                className={`Button Field-button${fieldSelected === item.variation ? ' Field-button--active' : ''}`}
                data-field={item.text}
              >
                <div className="Field-buttonImageContainer">
                  <img src={item.button?.src} alt="field" />
                </div>
                {/* Dugout shows the pitch size under each field thumbnail (11x11 / 8x8 / 6x6 /
                    Futsal). Gated so the other themes' field buttons stay exactly as-is. Text is the
                    raw `item.text` dimension label — not localised, matching the Dugout tool. */}
                {ToolContextState.isDugoutTheme && (
                  <span className="Field-buttonLabel">{item.text}</span>
                )}
              </button>
            </div>
          ))}
      </div>
    </section>
  );
};

export default Fields;
